import {ChangeDetectionStrategy, Component, inject, NgZone, OnInit} from '@angular/core';
import {
  BezoekschipOrganisation,
  Country,
  CustomsOffice,
  GetOrganisationByShortName,
  GetVisit,
  IE3AdditionalSupplyChainActor,
  IE3CL704,
  IE3CL750,
  IE3ConsignmentMasterLevel,
  IE3ConsignmentProcess,
  IE3CustomsProcess,
  IE3CustomsStatus,
  IE3EnsStatus,
  IE3FilingType,
  IE3FindCustomsOfficesFirstEuCountry,
  IE3GetConsignment,
  IE3GetEnsLocalReferenceNumber,
  IE3GetEnsLocalReferenceNumberResponse,
  IE3HouseConsignment,
  IE3InvalidateConsignment,
  IE3Party,
  IE3Place,
  IE3SaveConsignment,
  IE3SupportingDocument,
  IE3TransportEquipment,
  IE3Vehicle,
  Location,
  Port,
  TaskMessageStatus,
  Terminal,
  Vessel,
  Visit,
  VisitSummary,
  WarehouseLicense
} from "@portbase/bezoekschip-service-typescriptmodels";
import {
  checkValidity,
  clearValidation,
  cloneObject,
  filterByTerm,
  formDataSaved,
  isDefined,
  nonNull,
  openConfirmationModalWithCallback,
  removeItem,
  sendCommand,
  sendQuery
} from "../../../../common/utils";
import {ConsignmentRouteInfo} from "./master-consignment-route/master-consignment-route.component";
import {AppContext} from "../../../../app-context";
import {
  ConsignmentUtils,
  EquipmentWithPlacement,
  GoodsItemSummaryWithHouseConsignments,
} from "../../consignment.utils";
import {PortvisitUtils} from "../../../../refdata/portvisit-utils";
import {combineLatest, Observable, of, ReplaySubject, Subject} from "rxjs";
import {WebsocketService} from "../../../../common/websocket.service";
import lodash, {cloneDeep} from "lodash";
import {map, mergeMap, tap} from "rxjs/operators";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../../common/modal/modal-confirm.component";
import typesOfMeansOfTransport from "../tables/consignment-vehicles-table/types-of-means-of-transport.json";
import {HouseConsignmentDetailsComponent} from "../house-consignment-details/house-consignment-details.component";
import {VisitContext} from "../../../../visit-details/visit-context";
import {ConsignmentRules, PortItinerary} from "../../consignment.rules";
import {HandleEvent} from "../../../../common/handlers/handle-event";
import {View} from "../../../../common/handlers/view";
import {Handler} from "../../../../common/handlers/handler";
import {HandleQuery} from "../../../../common/handlers/handle-query";
import {GetErrorPointers} from "../../../handlers/consignment-handler";
import {publishHandlerEvent} from "../../../../common/handlers/app-common-utils";
import {ErrorPointer, ErrorWithPointers} from "../../../../common/errors/errors";
import {TabInfo} from "../../../../common/tab-panel/tab-panel.component";

@Component({
    selector: 'app-master-consignment-details',
    templateUrl: './master-consignment-details.component.html',
    styleUrls: ['./master-consignment-details.component.scss'],
    providers: [WebsocketService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class MasterConsignmentDetailsComponent extends View implements OnInit {
  protected readonly appContext = AppContext;
  private websocket: WebsocketService<IE3ConsignmentProcess> = inject(WebsocketService<IE3ConsignmentProcess>);
  private ngZone: NgZone = inject(NgZone);
  protected readonly MainFilingChoice = MainFilingChoice;
  protected readonly ConsignmentRules = ConsignmentRules;

  utils = ConsignmentUtils;
  rules = ConsignmentRules;
  refData = PortvisitUtils;
  consignmentProcess: IE3ConsignmentProcess;
  data: MasterConsignmentDetailsComponentData;
  countries: Map<string, Country>;
  isNewConsignment: boolean;
  editMode: boolean;
  customsOffices: CustomsOffice[] = [];
  _routes: ConsignmentRouteInfo[] = [];
  showEnsLrn: boolean;
  ensLrn: string;
  ensMrn: string;
  showTsLrn: boolean;
  mainFilingChoice: MainFilingChoice = MainFilingChoice.NO;
  billOfLadingFilingType: BillOfLadingFilingType = {};
  allTypeOfMeansOfTransportKeys = Object.keys(typesOfMeansOfTransport);
  billOfLadingFilingTypeMapper: { [index: string]: BillOfLadingFilingType } = {
    [IE3FilingType.F10]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: BillOfLadingType.Straight,
      filing: Filing.Single
    },
    [IE3FilingType.F11]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: BillOfLadingType.Master,
      filing: Filing.Single
    },
    [IE3FilingType.F12]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: BillOfLadingType.Master,
      filing: Filing.Multiple
    },
    [IE3FilingType.F13]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: BillOfLadingType.Straight,
      filing: Filing.Multiple
    },
    [IE3FilingType.F50]: {modeOfTransportType: ModeOfTransportType.Road, billOfLadingType: null, filing: Filing.Single},
    [IE3FilingType.TSD]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: null,
      filing: Filing.Single
    },
    [IE3FilingType.NONE]: {
      modeOfTransportType: ModeOfTransportType.Maritime,
      billOfLadingType: BillOfLadingType.Master,
      filing: Filing.Multiple
    },
  };
  typeOfIdentifications: IE3CL750[] = [IE3CL750.N10, IE3CL750.N20, IE3CL750.N21, IE3CL750.N30, IE3CL750.N31, IE3CL750.N41, IE3CL750.N80];
  billOfLadingTypes: BillOfLadingType[] = Object.values(BillOfLadingType);
  modeOfTransportTypes: ModeOfTransportType[] = Object.values(ModeOfTransportType);
  mainFilingChoiceTypes: MainFilingChoice[] = Object.values(MainFilingChoice);
  filingTypes: Filing[] = Object.values(Filing);

  houseConsignments: IE3HouseConsignment[];
  equipments: EquipmentWithPlacement[];
  goodsItems: GoodsItemSummaryWithHouseConsignments[] = [];
  terminalsOfVisit: Observable<Terminal[]>;
  itineraryOfVisit: Observable<PortItinerary[]>;
  itineraryAfterVisit: Observable<PortItinerary[]>;
  portItinerary: PortItinerary[];
  portVisit: PortItinerary;
  isPassingThrough: boolean;
  private initialised: boolean;
  errors: ErrorWithPointers[];
  errorGoodsItemNumbers: Set<number> = new Set();
  errorEquipmentNumbers: Set<string> = new Set();
  errorHouseConsignmentNumbers: Set<string> = new Set();
  consignmentProcessSubject: Subject<IE3ConsignmentProcess>;
  protected entryCrnRequired: boolean;
  protected hasEntryCrn: boolean;
  protected portEnteringEu: Port;
  masterLevelPredicate: (e: ErrorWithPointers) => boolean = e => e.pointers.some(p => p.type === "masterLevel")
    && !e.pointers.some(p => p.type === "houseLevel")
    && !e.pointers.some(p => p.type === "goodsItem")
    && !e.pointers.some(p => p.type === "equipment");
  houseLevelPredicate: (e: ErrorPointer) => boolean = e => e.type === "houseLevel";
  partyPredicate: (e: ErrorPointer) => boolean = (p => p.screenPointer === "Parties");
  goodsItemPredicate: (e: ErrorPointer) => boolean = (p => p.type === "goodsItem");
  equipmentPredicate: (e: ErrorPointer) => boolean = (p => p.type === "equipment");
  private selectedTab: TabInfo;
  protected visit: Visit;

  constructor() {
    super();
    this.consignmentProcessSubject = new ReplaySubject(1);
  }

  bulkAuthorisationFormatter = (value: BezoekschipOrganisation[]) => value.map(PortvisitUtils.bulkAuthorisationFormatter).join(", ");
  searchVessel = term => sendQuery('com.portbase.bezoekschip.common.api.visit.FindVessels', {term: term})
    .pipe(map((results: Vessel[]) => {
      return lodash.flatMap(results, vessel => <IE3Vehicle>{
        name: vessel.name,
        identificationNumber: vessel.imoCode,
        typeOfMeansOfTransport: vessel.motUnCode,
        nationality: vessel.flagCountryUnCode,
        typeOfIdentification: IE3CL750.N10
      });
    }));
  vehicleFormatter = (vehicle: IE3Vehicle) => vehicle?.name ? vehicle.name + ' – ' + vehicle.identificationNumber : '';

  getCustomsOfficeProviders = (): Observable<CustomsOffice[]> => {
    return (this.consignmentProcess?.consignmentMasterLevel.activeBorderTransportMeans?.itinerary?.filter(nonNull)?.length ?
      sendQuery('com.portbase.bezoekschip.common.api.consignments.queries.FindCustomsOfficesFirstEuCountry', <IE3FindCustomsOfficesFirstEuCountry>{
        countryUnCodes: this.countriesForCustomsOfficeOfFirstEntry
      }) : of([]));
  };

  findInPreviousPorts = term => this.itineraryOfVisit
    .pipe(map(t => t.filter(filterByTerm(term)).map(t => (<Location> {
      name: t.port.name,
      countryUnCode: t.port.countryUnCode,
      euLocation: t.port.euPort,
      locationUnCode: t.port.locationUnCode
    }))));

  findInCurrentAndNextPorts = term => this.itineraryAfterVisit
    .pipe(map(t => t.filter(filterByTerm(term)).map(t => (<Location> {
      name: t.port.name,
      countryUnCode: t.port.countryUnCode,
      euLocation: t.port.euPort,
      locationUnCode: t.port.locationUnCode
    }))));

  refreshCustomsOfficeProvider = () => this.getCustomsOfficeProviders()
    .subscribe(c => this.customsOffices = [null].concat(c));

  ngOnInit(): void {
    this.isNewConsignment = !this.data.consignmentProcessId && !this.data.cachedConsignmentProcess;
    if (this.isNewConsignment) {
      this.editMode = true;
      if (this.data.copyOfConsignmentProcessId) {
        sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.GetConsignment", <IE3GetConsignment>{
          consignmentProcessId: this.data.copyOfConsignmentProcessId
        }, {
          caching: false
        }).subscribe((c: IE3ConsignmentProcess) => {
          this.consignmentProcess = c;
          this.hasEntryCrn = !!c?.filing?.entryCrn;
          this.consignmentProcess.status = null;
          this.consignmentProcess.cancelled = false;
          this.consignmentProcess.ensStatus = IE3EnsStatus.UNKNOWN;
          this.consignmentProcess.ensTaskStatus = TaskMessageStatus.UNKNOWN;
          this.consignmentProcess.consignmentMasterLevel.consignmentNumber = null;
          this.consignmentProcess.consignmentMasterLevel.consignmentsHouseLevel?.forEach(value => value.consignmentNumber = null);
          this.updateFromConsignment(this.consignmentProcess, true);
        });
      } else {
        this.consignmentProcess = this.createNewConsignment(this.data.transportEquipmentMap, this.data.crn);
        this.updateFromConsignment(this.consignmentProcess);
      }
    } else {
      this.websocket.initialise(`/api/ui/${btoa(this.data.consignmentProcessId)}`,
        (update: IE3ConsignmentProcess) => this.updateFromConsignment(update), true);
      if (!this.data.cachedConsignmentProcess) {
        sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.GetConsignment", <IE3GetConsignment>{
          consignmentProcessId: this.data.consignmentProcessId
        }, {
          caching: false
        }).subscribe(c => this.updateFromConsignment(c, true));
      } else {
        this.updateFromConsignment(this.data.cachedConsignmentProcess, true);
      }
    }
    ConsignmentUtils.getCountries().subscribe(c => {
      this.countries = c;
      this.updateRoutes();
    });
    this.getConsignmentErrors()?.subscribe(errors => {
      this.errors = errors;
      this.errorGoodsItemNumbers = new Set(
        errors?.flatMap(e => e.pointers?.filter(p => p.type === 'goodsItem').map(p => parseInt(p.identification))) || []
      );
      this.errorEquipmentNumbers = new Set(
        errors?.flatMap(e => e.pointers?.filter(p => p.type === 'equipment').map(p => p.identification)) || []
      );
      this.errorHouseConsignmentNumbers = new Set(
        errors?.flatMap(e => e.pointers?.filter(p => p.type === 'houseLevel').map(p => p.identification)) || []
      );
    });
  }

  get masterLevel(): IE3ConsignmentMasterLevel {
    return this.consignmentProcess.consignmentMasterLevel;
  }

  get itinerary(): string[] {
    return this.masterLevel.activeBorderTransportMeans.itinerary;
  }

  onPlaceOfLoadingChange = (placeOfLoading: IE3Place) => {
    this.masterLevel.placeOfLoading = placeOfLoading;
    this.tryUpdateDepartureTimes(placeOfLoading);
    this.updateRoutes();
    this.updateFilingType();
  }

  onPlaceOfUnloadingChange = (placeOfUnloading: IE3Place) => {
    this.masterLevel.placeOfUnloading = placeOfUnloading;
    this.masterLevel.dischargeTerminal = null;
    this.masterLevel.terminalLicense = null;
    this.updateRoutes();
    this.updateFilingType();
  }

  onTerminalChange = (terminal: Terminal) => {
    this.masterLevel.terminalLicense = null;
    if (terminal?.terminalLicence) {
      this.masterLevel.terminalLicense = terminal.terminalLicence;
    }
    if (!this.masterLevel.terminalLicense && terminal?.organisationShortName) {
      sendQuery("com.portbase.bezoekschip.common.api.authorisation.GetOrganisationByShortName", <GetOrganisationByShortName>{
        shortName: terminal.organisationShortName
      }, {
        caching: true
      }).subscribe((t: BezoekschipOrganisation) => {
        this.masterLevel.terminalLicense = t?.rtoNumber || terminal.terminalCode;
        if (!this.masterLevel.terminalLicense && t?.customsEORINumber) {
          PortvisitUtils.findWarehouseLicenses(t.customsEORINumber)
            .subscribe((licenses: WarehouseLicense[]) => this.masterLevel.terminalLicense = licenses?.length
              ? licenses[0].licenseNumber : null);
        }
      });
    }
    this.updateRoutes();
  }

  onCustomStatusChange = (customsStatus: IE3CustomsStatus) => {
    if (customsStatus === IE3CustomsStatus.EU_COMMUNITY_GOODS_IN_TRANSHIPMENT && !this.masterLevel.customsProcess) {
      this.masterLevel.customsProcess = IE3CustomsProcess.SEA_IN_SEA_OUT;
    }
  }

  onTabChange(selectedTab: TabInfo) {
    this.selectedTab = selectedTab;
  }

  private tryUpdateDepartureTimes = (placeOfLoading: IE3Place) => {
    if (placeOfLoading) {
      this.itineraryOfVisit?.subscribe(i => {
        const portItinerary = i.reduce((a, b) =>
          b.port.locationUnCode === placeOfLoading.locationUnCode ? b : a, i[0]);
        if (portItinerary) {
          this.masterLevel.activeBorderTransportMeans.estimatedDateAndTimeOfDeparture = portItinerary.departure;
        }
      });
    }
  }

  private updateFromConsignment = (c: IE3ConsignmentProcess, updateEditMode?: boolean) => {
    this.consignmentProcess = c;
    if (c.filing?.declarant?.iamConnectedId === AppContext.userProfile.organisation?.iamConnectedId) {
      this.consignmentProcess.filing.declarant = AppContext.userProfile.organisation;
    }
    this.mainFilingChoice = c.filing.filingType == IE3FilingType.NONE ? MainFilingChoice.YES : MainFilingChoice.NO;
    if (updateEditMode && !ConsignmentUtils.hasBeenDeclared(c) && this.isEditable) {
      this.editMode = true;
    }
    if (c.filing.crn) {
      this.updateDataFromVisit(c.filing.crn, false);
    } else {
      this.entryCrnRequired = false;
    }
    this.consignmentProcess.status = this.consignmentProcess.status || {};
    this.masterLevel.previousDocument = this.masterLevel.previousDocument || {};
    this.masterLevel.transportCharges = this.masterLevel.transportCharges || {};
    this.masterLevel.exchangeRate = this.masterLevel.exchangeRate || {};
    this.masterLevel.bulkAuthorisations = this.masterLevel.bulkAuthorisations || [];
    this.masterLevel.activeBorderTransportMeans = this.masterLevel.activeBorderTransportMeans || {
      vehicle: {},
      itinerary: []
    };
    this.masterLevel.goodsItems = this.masterLevel.goodsItems || [];
    this.masterLevel.consignmentsHouseLevel = this.masterLevel.consignmentsHouseLevel || [];
    this.ensMrn = this.consignmentProcess.status.filingStatusENS?.ensRegistrationResponse?.mrn;
    this.billOfLadingFilingType = cloneObject(this.billOfLadingFilingTypeMapper[c.filing.filingType]) || {};
    if (c.filing.filingType === IE3FilingType.TSD) {
      this.billOfLadingFilingType.billOfLadingType = isDefined(c.filing.straight) ? c.filing.straight
        ? BillOfLadingType.Straight : BillOfLadingType.Master : null;
    }
    const ensStatus = this.consignmentProcess.status.filingStatusENS;
    this.showEnsLrn = ensStatus?.filingTime != null && !ensStatus?.ensRegistrationResponse?.mrn;
    if (this.showEnsLrn) {
      sendQuery("com.portbase.bezoekschip.common.api.consignments.queries.GetEnsLocalReferenceNumber", <IE3GetEnsLocalReferenceNumber> {
        consignmentProcessId: this.consignmentProcess.consignmentProcessId
      }).subscribe((c: IE3GetEnsLocalReferenceNumberResponse) => this.ensLrn = c.lrn);
    }
    const tsStatus = this.consignmentProcess.status.filingStatusTemporaryStorage;
    this.showTsLrn = tsStatus?.ics2Filing && tsStatus?.registrationTime != null && !tsStatus?.registrationResponse?.mrn;
    this.refreshCustomsOfficeProvider();
    this.updateRoutes();
    this.masterLevel.transportCharges = this.masterLevel.transportCharges || {};
    this.equipments = Object.entries(this.masterLevel.transportEquipmentMap)
      .map(([containerIdentificationNumber, equipment]) => (<EquipmentWithPlacement>{
        equipment: equipment,
        placement: null,
        goodsItems: ConsignmentUtils.getGoodsOfEquipment(this.consignmentProcess, containerIdentificationNumber),
        houseConsignments: this.masterLevel.consignmentsHouseLevel
          .filter(h => ConsignmentUtils.getEquipmentNumbersOfHouseConsignment(
            this.consignmentProcess, h).includes(containerIdentificationNumber))
          .map(h => h.consignmentNumber)
      }));
    this.houseConsignments = this.masterLevel.consignmentsHouseLevel
      .map(h => {
        const equipmentList = ConsignmentUtils.getEquipmentNumbersOfHouseConsignment(
          this.consignmentProcess, h).map(
          e => this.masterLevel.transportEquipmentMap[e]);
        return {
          consignmentNumber: h.consignmentNumber,
          consignee: h.consignee,
          consignor: h.consignor,
          goodsItems: h.goodsItems.map(g => ({
            goodsItemNumber: g.goodsItemNumber,
            placements: g.goodsPlacements,
            grossWeight: g.grossWeight,
            commodity: g.commodity
          })),
          equipments: equipmentList.map(t => ({
            equipmentNumber: t.containerIdentificationNumber,
            empty: t.empty,
            sizeType: t.containerSizeAndType
          }))
        }
      });
    this.goodsItems = ConsignmentUtils.toConsignmentGoodsItemsSummaries(this.masterLevel.goodsItems)
      .map(g => (<GoodsItemSummaryWithHouseConsignments>{
        goodsItem: g,
        houseConsignments: this.masterLevel.consignmentsHouseLevel
          .filter(h => h.goodsItems.some(
            hg => hg.commodity?.commodityCode?.harmonizedSystemSubHeadingCode === g.commodity?.commodityCode?.harmonizedSystemSubHeadingCode))
          .map(h => h.consignmentNumber),
        clearance: ConsignmentUtils.getClearanceOfGoodsItem(this.consignmentProcess.status, g.goodsItemNumber)
      }));
    this.initialised = true;
    this.updateFilingType();
    this.consignmentProcessSubject.next(this.consignmentProcess);
  }

  getRoutesOfConsignment = (): Observable<ConsignmentRouteInfo[]> => {
    if (!this.consignmentProcess || !this.countries) {
      return of([]);
    }
    if (this.itineraryOfVisit) {
      return combineLatest([this.itineraryOfVisit, this.itineraryAfterVisit])
        .pipe(tap(s => this.masterLevel.activeBorderTransportMeans.itinerary = this.getItineraryBasedOnVisit(s[0], s[1])))
        .pipe(mergeMap(v => this.getCustomsOfficeProviders()))
        .pipe(map(c => {
          this.customsOffices = c;
          return this.createRoutesInfo();
        }));
    }
    this.masterLevel.activeBorderTransportMeans.itinerary = this.getItineraryOutsideDutchPort();
    return of(this.createRoutesInfo());
  }

  private getItineraryOutsideDutchPort = (): string[] => {
    const itinerary = cloneDeep(this.masterLevel.activeBorderTransportMeans.itinerary);
    const placeOfAcceptance = this.masterLevel.placeOfAcceptance;
    const placeOfLoading = this.masterLevel.placeOfLoading;
    const placeOfUnloading = this.masterLevel.placeOfUnloading;
    const placeOfDelivery = this.masterLevel.placeOfDelivery;
    if (placeOfAcceptance && !itinerary.includes(placeOfAcceptance.countryUnCode)) {
      itinerary.unshift(placeOfAcceptance.countryUnCode);
    }
    const indexOfAcceptance = placeOfAcceptance ? itinerary.indexOf(placeOfAcceptance.countryUnCode) : -1;
    if (placeOfLoading && !itinerary.includes(placeOfLoading.countryUnCode)) {
      itinerary.splice(indexOfAcceptance + 1, 0, placeOfLoading.countryUnCode);
    }
    if (placeOfDelivery && !itinerary.includes(placeOfDelivery.countryUnCode)) {
      itinerary.push(placeOfDelivery.countryUnCode);
    }
    const indexPlaceOfDelivery = placeOfDelivery ? itinerary.indexOf(placeOfDelivery.countryUnCode) : -1;
    if (placeOfUnloading && !itinerary.includes(placeOfUnloading.countryUnCode)) {
      indexPlaceOfDelivery > -1 ? itinerary.splice(indexPlaceOfDelivery, 0, placeOfUnloading.countryUnCode)
        : itinerary.push(placeOfUnloading.countryUnCode);
    }
    return itinerary;
  }

  private getItineraryBasedOnVisit = (portItinerary: PortItinerary[], itineraryAfterVisit: PortItinerary[]): string[] => {
    const placeOfLoading = this.consignmentProcess.consignmentMasterLevel.placeOfLoading?.countryUnCode;
    const placeOfUnloading = this.consignmentProcess.consignmentMasterLevel.placeOfUnloading?.countryUnCode;
    const portCountries = portItinerary.map(p => p.port.countryUnCode);
    const portCountriesWithNextPort = itineraryAfterVisit.map(p => p.port.countryUnCode);

    // find the first occurrence of placeOfUnloading in all ports including next port
    const endIndex = portCountriesWithNextPort.indexOf(placeOfUnloading);
    if (endIndex === -1) {
      return [];
    }

    // find the last occurrence of placeOfLoading strictly before that end
    const startIndex = portCountries.lastIndexOf(placeOfLoading);
    if (startIndex === -1) {
      return [];
    }

    let prunedItinerary = portCountries.concat(portCountriesWithNextPort).slice(startIndex, portCountries.length + endIndex + 1);
    if (prunedItinerary.length > 2) {
      return this.getUniqueConsecutive(prunedItinerary);
    } else {
      return prunedItinerary;
    }
  }

  private createRoutesInfo = (): ConsignmentRouteInfo[] => {
    return ConsignmentUtils.createRoutesInfo(this.consignmentProcess, this.countries, this.masterLevel.activeBorderTransportMeans.itinerary,
      this.masterLevel.placeOfAcceptance, this.masterLevel.placeOfDelivery);
  }

  private updateEntryCrnRequired(v: Visit): void {
    this.entryCrnRequired = ConsignmentUtils.isEntryCrnRequired(this.consignmentProcess, v);
    this.portEnteringEu = ConsignmentRules.getPortEnteringEu(v, this.consignmentProcess);
  }

  get equipmentSupported(): boolean {
    return !this.consignmentProcess.filing.filingType || [IE3FilingType.F11, IE3FilingType.F12, IE3FilingType.TSD,
      IE3FilingType.NONE].includes(this.consignmentProcess.filing.filingType);
  }

  get routes(): ConsignmentRouteInfo[] {
    return this._routes;
  }

  set routes(routes: ConsignmentRouteInfo[]) {
    this.masterLevel.activeBorderTransportMeans.itinerary = routes.map(r => r.country?.code);
    this.refreshCustomsOfficeProvider();
    this.updateRoutes();
    this.updateFilingType();
  }

  updatePreviousDocument = (documentIdentification: string) => {
    this.masterLevel.previousDocument.identification = documentIdentification;
    this.updateFilingType();
  }

  addRoute = () => {
    this._routes.push({
      country: {}
    });
  }

  remove = () => {
    openConfirmationModalWithCallback((confirmed) => {
      if (confirmed) {
        sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.InvalidateConsignment", <IE3InvalidateConsignment>{
          consignmentProcessId: this.consignmentProcess.consignmentProcessId,
          consignmentNumber: this.masterLevel.consignmentNumber,
          cargoDeclarantId: this.consignmentProcess.filing.declarant.iamConnectedId
        });
      }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
      type: "danger",
      title: "Remove consignment",
      message: "You are about to remove consignment: " + this.masterLevel.consignmentNumber,
      question: "Are you sure that you want to remove this consignment?",
      confirmText: "Yes",
      cancelText: "No"
    }, 'static');
  }

  cancel = () => {
    clearValidation(this.elementRef);
    this.editMode = !this.editMode && !this.consignmentProcess.fromVisit;
  }

  toggleEdit = () => {
    if (checkValidity(this.elementRef)) {
      this.editMode = !this.editMode && !this.consignmentProcess.fromVisit;
    }
  }

  startEditing = () => this.editMode = true

  save = () => {
    if (this.isNewConsignment) {
      this.websocket.initialise(`/api/ui/${btoa(this.consignmentProcess.consignmentProcessId)}`,
        (update: IE3ConsignmentProcess) => this.updateFromConsignment(update), true);
    }
    this.processModel(false);
    const consignment = cloneDeep(this.masterLevel);
    sendCommand("com.portbase.bezoekschip.common.api.consignments.commands.SaveConsignment", <IE3SaveConsignment>{
      consignment: consignment,
      filing: this.consignmentProcess.filing
    }, () => {
      AppContext.registerSuccess("Consignment saved successfully");
      formDataSaved();
      this.editMode = false;
    })
  }

  declare = () => {
    if (this.isNewConsignment) {
      this.websocket.initialise(`/api/ui/${btoa(this.consignmentProcess.consignmentProcessId)}`,
        (update: IE3ConsignmentProcess) => this.updateFromConsignment(update), true);
    }
    if (this.processModel(true)) {
      if (checkValidity(this.elementRef)) {
        ConsignmentUtils.declareConsignment(this.consignmentProcess, this.visit);
      } else if (ConsignmentUtils.allowedToEdit()) {
        this.editMode = !this.consignmentProcess.cancelled;
        publishHandlerEvent("validationFailed");
      }
    }
  }

  processModel = (registerErrors: boolean) => ConsignmentUtils.validateMasterConsignment(
    this.consignmentProcess, this.visit, registerErrors);

  createNewConsignment = (transportEquipment?: { [containerIdentificationNumber: string]: IE3TransportEquipment },
                          crn?: string): IE3ConsignmentProcess => {
    return ({
      consignmentMasterLevel: {
        activeBorderTransportMeans: {
          vehicle: {},
          itinerary: []
        },
        previousDocument: {},
        transportEquipmentMap: transportEquipment || {},
        goodsItems: [],
        consignmentsHouseLevel: [],
      },
      filing: {
        declarant: AppContext.userProfile.organisation,
        filingType: null,
        crn: crn
      }
    })
  }

  get isEditable() {
    return ConsignmentUtils.isEditable(this.consignmentProcess);
  }

  get isEditableForTab() {
    return this.isEditable && this.selectedTab?.index <= 1;
  }

  get isAllowedToDeclare() {
    return ConsignmentRules.isAllowedToDeclare(this.consignmentProcess);
  }

  get ensEnabledOfDeclarant(): boolean {
    const declarant = this.consignmentProcess?.filing?.declarant ? this.consignmentProcess.filing.declarant : AppContext.userProfile.organisation;
    return declarant.ensEnabled == undefined || declarant.ensEnabled;
  }

  isEnsInternationalEnabledForDeclarant(): boolean {
    const declarant = this.consignmentProcess?.filing?.declarant ? this.consignmentProcess.filing.declarant : AppContext.userProfile.organisation;
    return declarant.settings ? declarant.settings.declaresEnsInternational : false;
  }

  get countriesForCustomsOfficeOfFirstEntry(): string[] {
    if (this.portItinerary?.length) {
      return lodash.takeRight(this.portItinerary.concat(this.portVisit), 2).map(p => p?.port?.countryUnCode);
    }
    const itinerary = this.itinerary;
    const placeOfLoading = this.consignmentProcess?.consignmentMasterLevel?.placeOfLoading;
    const indexPlaceOfLoading = placeOfLoading ? this.itinerary.indexOf(itinerary.find(i => i === ConsignmentUtils.getCountryCode(placeOfLoading))) : -1;
    return this.itinerary.slice(indexPlaceOfLoading);
  }

  addSupportingDocument = () => {
    if (!this.masterLevel.supportingDocuments?.length) {
      this.masterLevel.supportingDocuments = [];
    }
    this.masterLevel.supportingDocuments.push({});
  };

  deleteSupportingDocument = (item: IE3SupportingDocument) => removeItem(this.masterLevel.supportingDocuments, item);

  setConsignmentProcessId = (consignmentNumber: string) => {
    this.consignmentProcess.consignmentProcessId = `${AppContext.userProfile.organisation.iamConnectedId}_${consignmentNumber}`;
  }

  billOfLadingTypeChanged = (value: BillOfLadingType) => {
    if (value === BillOfLadingType.Straight && this.masterLevel.consignmentsHouseLevel?.length > 1) {
      AppContext.registerError("Unable to change from master to straight because there are more then 1 house B/Ls in this consignment. For straight there is only 1 house B/L possible");
      setTimeout(() => {
        this.billOfLadingFilingType.billOfLadingType = BillOfLadingType.Master;
        this.updateFilingType();
      }, 0);
      return;
    }
    this.billOfLadingFilingType.billOfLadingType = value;
    const hasMasterData = (): boolean => !!this.masterLevel.consignee || !!this.masterLevel.consignor
      || !!this.masterLevel.goodsItems?.length;
    if (value === BillOfLadingType.Straight && hasMasterData() && !this.masterLevel.consignmentsHouseLevel?.length) {
      this.migrateMasterDataToHouse();
      this.updateFromConsignment(this.consignmentProcess);
    }
    this.updateFilingType();
  }

  migrateMasterDataToHouse = () => {
    const houseLevel = HouseConsignmentDetailsComponent.createHouseConsignment(this.masterLevel);
    houseLevel.goodsItems = this.masterLevel.goodsItems;
    this.masterLevel.consignmentsHouseLevel.push(houseLevel);
    this.masterLevel.goodsItems = [];
    this.masterLevel.consignor = null;
    this.masterLevel.consignee = null;
  }

  updateFilingType = (): void => {
    this.ngZone.runOutsideAngular(() => {
      if (this.mainFilingChoice == MainFilingChoice.YES) {
        this.consignmentProcess.filing.straight = false;
        this.consignmentProcess.filing.filingType = IE3FilingType.NONE;
        this.billOfLadingFilingType = this.billOfLadingFilingTypeMapper[IE3FilingType.NONE]
        return;
      } else {
        this.updateFilingTypeBasedOnBillOfLadingTypes();
      }
      const isMilitary = this.masterLevel.customsProcess === IE3CustomsProcess.MILITARY;
      if (this.ensEnabledOfDeclarant && ConsignmentRules.ensRequiredBasedOnItineraryOrPreviousDocument(this.visit, this.consignmentProcess) && !isMilitary) {
        this.handleEnsOrDefaultCondition();
      } else if (isMilitary || this.isPassingThrough || ConsignmentRules.isDutchPort(this.consignmentProcess)) {
        this.handleDutchPortCondition();
      } else if (this.isEnsInternationalEnabledForDeclarant()) {
        this.handleEnsOrDefaultCondition();
      }
      this.consignmentProcess.filing.straight = ConsignmentRules.isTemporaryStorageOnlyConsignment(this.consignmentProcess.filing.filingType)
        && this.billOfLadingFilingType.billOfLadingType === BillOfLadingType.Straight;
    });
  }

  private handleEnsOrDefaultCondition(): void {
    if (this.billOfLadingFilingType.modeOfTransportType === ModeOfTransportType.Road) {
      this.consignmentProcess.filing.filingType = IE3FilingType.F50;
      this.billOfLadingFilingType = cloneObject(this.billOfLadingFilingTypeMapper[this.consignmentProcess.filing.filingType]) || {};
    }
  }

  private handleDutchPortCondition(): void {
    this.consignmentProcess.filing.filingType = IE3FilingType.TSD;
    const billOfLadingFilingType = cloneDeep(
      this.billOfLadingFilingTypeMapper[this.consignmentProcess.filing.filingType]
    );
    billOfLadingFilingType.billOfLadingType = this.consignmentProcess.filing.filingType === IE3FilingType.TSD
      ? ConsignmentRules.isRoadConsignment(this.consignmentProcess) ? BillOfLadingType.Master
        : this.billOfLadingFilingType.billOfLadingType || (this.consignmentProcess.filing.straight
        ? BillOfLadingType.Straight : BillOfLadingType.Master)
      : BillOfLadingType.Master;
    this.billOfLadingFilingType = billOfLadingFilingType;
    this.billOfLadingTypes = this.consignmentProcess.filing.filingType === IE3FilingType.TSD
      ? [BillOfLadingType.Master, BillOfLadingType.Straight]
      : [BillOfLadingType.Master];
  }

  private updateFilingTypeBasedOnBillOfLadingTypes(): void {
    this.billOfLadingFilingType.filing = this.billOfLadingFilingType.filing || Filing.Single;
    const foundEntry = Object.entries(this.billOfLadingFilingTypeMapper)
      .find(e => lodash.isEqual(e[1], this.billOfLadingFilingType));
    this.consignmentProcess.filing.filingType = foundEntry ? foundEntry[0] as IE3FilingType : null;
  }

  addNotifyParty = (): void => {
    const parties = this.masterLevel.notifyParties || [];
    parties.push((<IE3Party>{["isNewRecord"]: true, name: "", address: {}, communications: []}) as any);
    this.masterLevel.notifyParties = parties;
  }

  checkPartyDeletion = (party: IE3Party, index: number) => {
    if (!party) {
      this.masterLevel.notifyParties.splice(index, 1);
    }
  }

  addSupplyChainActor = (type: IE3CL704): void => {
    const actors = this.masterLevel.additionalSupplyChainActors || [];
    actors.push({type: type, ["isNewRecord"]: true} as any);
    this.masterLevel.additionalSupplyChainActors = actors;
  }

  addBulkAuthorisation = (): void => {
    const organisations = this.masterLevel.bulkAuthorisations || [];
    organisations.push({["isNewRecord"]: true} as any);
    this.masterLevel.bulkAuthorisations = organisations;
  }

  checkSupplyChainActorDeletion = (actor: IE3AdditionalSupplyChainActor, index: number) => {
    if (!actor) {
      this.masterLevel.additionalSupplyChainActors.splice(index, 1);
    }
  }

  checkBulkAuthorisationDeletion = (organisation: BezoekschipOrganisation, index: number) => {
    if (!organisation) {
      this.masterLevel.bulkAuthorisations.splice(index, 1);
    }
  }

  showDetails = (): boolean => !this.isNewConsignment || !!this.consignmentProcess?.filing?.filingType;

  onCrnChange = (value: VisitSummary) => {
    if (value) {
      this.updateDataFromVisit(value.crn, true);
    } else {
      this.updateVisitFields(null);
    }
  }

  private setTerminalsOfVisit(v: Visit): Observable<Terminal[]> {
    return this.terminalsOfVisit = of(v.visitDeclaration.portVisit.berthVisits.filter(v => !!v.berth).map(v => v.berth))
      .pipe(mergeMap(terminals => sendQuery("com.portbase.bezoekschip.common.api.visit.GetTerminals", {
        portUnCode: v.portOfCall.port.locationUnCode,
        terminalCodes: terminals.map(t => t.terminalCode)
      }, {caching: true})));
  }

  private setPortItinerary = (v: Visit) => {
    this.portItinerary = v.visitDeclaration.previousPorts.filter(p => p.port).map(p => (<PortItinerary>{
      port: p.port,
      departure: p.departure,
      arrival: p.arrival
    })).reverse();
    this.portVisit = {
      port: v.portOfCall.port,
      arrival: v.visitDeclaration.portVisit.etaPort,
      departure: v.visitDeclaration.portVisit.etdPort
    };
    this.itineraryOfVisit = of(this.portItinerary);
    this.itineraryAfterVisit = of([this.portVisit].concat(v.visitDeclaration.nextPorts.filter(p => p.port).map(p => (<PortItinerary>{
      port: p.port,
      departure: p.departure,
      arrival: p.arrival
    }))));
  }

  private updateDataFromVisit = (crn: string, forceUpdate: boolean) =>
    sendQuery("com.portbase.bezoekschip.common.api.visit.GetVisit", <GetVisit>{crn: crn})
      .subscribe((v: Visit) => {
        this.visit = v;
        this.setTerminalsOfVisit(v);
        this.setPortItinerary(v);
        this.updateVisitFields(v, forceUpdate);
      });

  private updateVisitFields(v?: Visit, forceUpdate: boolean = false) {
    const consignmentMasterLevel = this.masterLevel;
    const activeBorderTransportMeans = consignmentMasterLevel.activeBorderTransportMeans;
    this.consignmentProcess.filing.crn = v?.crn;
    if (v != null) {
      this.isPassingThrough = VisitContext.isPassingThrough(v);
      activeBorderTransportMeans.vehicle = <IE3Vehicle>{
        identificationNumber: v.vessel.imoCode,
        name: v.vessel.name,
        nationality: v.vessel.flagCountryUnCode,
        typeOfIdentification: IE3CL750.N10,
        typeOfMeansOfTransport: v.vessel.motUnCode
      };
      this.setTerminalsOfVisit(v).subscribe(t => {
        if (this.masterLevel.placeOfUnloading?.locationUnCode === v.portOfCall.port.locationUnCode) {
          this.masterLevel.dischargeTerminal = this.masterLevel.dischargeTerminal || (t?.length === 1 ? t[0] : null);
        } else {
          this.masterLevel.dischargeTerminal = this.masterLevel.terminalLicense = null;
        }
      });
      this.updateEntryCrnRequired(v);
    } else {
      this.isPassingThrough = false;
      this.itineraryOfVisit = this.itineraryAfterVisit = null;
      this.portItinerary = null;
      this.terminalsOfVisit = null;
      this.entryCrnRequired = false;
    }
    consignmentMasterLevel.carrier = forceUpdate ? v?.visitDeclaration.arrivalVoyage.carrier
      : consignmentMasterLevel.carrier || v?.visitDeclaration.arrivalVoyage.carrier;
    consignmentMasterLevel.agentVoyageNumber = forceUpdate ? v?.visitDeclaration.arrivalVoyage.voyageNumber
      : consignmentMasterLevel.agentVoyageNumber || v?.visitDeclaration.arrivalVoyage.voyageNumber;
    activeBorderTransportMeans.estimatedDateAndTimeOfArrival = activeBorderTransportMeans.estimatedDateAndTimeOfArrival
      || v?.visitDeclaration.portVisit.etaPort;
    consignmentMasterLevel.placeOfUnloading = forceUpdate ? this.convertPortToPlace(v?.portOfCall.port)
      : consignmentMasterLevel.placeOfUnloading || this.convertPortToPlace(v?.portOfCall.port);
    activeBorderTransportMeans.itinerary = this.getUniqueConsecutive(
      (v?.visitDeclaration.previousPorts.map(p => p?.port?.countryUnCode).reverse() || []).concat(
        consignmentMasterLevel.placeOfUnloading ? [consignmentMasterLevel.placeOfUnloading.countryUnCode] : []));
    this.updateRoutes();
    this.refreshCustomsOfficeProvider();
    if (this.initialised) {
      this.updateFilingType();
    }
  }

  private getUniqueConsecutive = (arr: string[]): string[] => {
    return arr.filter((item, index, self) => index === 0 || item !== self[index - 1]);
  }

  updateRoutes() {
    this.getRoutesOfConsignment().subscribe(r => this._routes = r);
  }

  get transportEquipmentCount(): number {
    return Object.keys(this.masterLevel.transportEquipmentMap).length;
  }

  private convertPortToPlace(port: Port | undefined): IE3Place {
    return port ? {
      name: port.name,
      countryUnCode: port.countryUnCode,
      euLocation: port.euPort,
      locationUnCode: port.locationUnCode
    } : null;
  }

  typeOfIdentificationFormatter = (value: IE3CL750) => {
    switch (value) {
      case IE3CL750.N10:
        return "Vessel";
      case IE3CL750.N20:
        return "Wagon Number";
      case IE3CL750.N21:
        return "Train Number";
      case IE3CL750.N30:
        return "Registration Number of the Road Vehicle";
      case IE3CL750.N31:
        return "Registration Number of the Road Trailer";
      case IE3CL750.N41:
        return "Registration Number of the Aircraft";
      case IE3CL750.N80:
        return "European Vessel Identification Number (ENI Code)";
      default:
        return value;
    }
  }

  typeOfMeansOfTransportFormatter = (code: string) => code && typesOfMeansOfTransport[code]
    ? `${code} - ${typesOfMeansOfTransport[code]}` : "";

  countryFormatter = (value: Country | string) => typeof value === "string" ? value : value.code;

  @HandleEvent("cachedConsignmentUpdated", {global: true})
  cachedConsignmentUpdated(c: IE3ConsignmentProcess) {
    if (this.consignmentProcess && c?.consignmentProcessId === this.consignmentProcess.consignmentProcessId) {
      this.updateFromConsignment(cloneDeep(c));
    }
  }

  @HandleQuery("getConsignment")
  getConsignment(): Observable<IE3ConsignmentProcess> {
    return this.consignmentProcessSubject;
  }

  @HandleQuery("getConsignmentErrors")
  getConsignmentErrors(): Observable<ErrorWithPointers[]> {
    return this.getConsignment().pipe(mergeMap(c => {
      return c ? this.sendQuery("getParsedConsignmentErrors", <GetErrorPointers>{
        consignmentProcess: c,
        taskStatus: c.ensTaskStatus,
        status: c.status
      }) : of(null);
    }));
  }

  @HandleQuery("getErrors")
  getErrors(): Observable<ErrorWithPointers[]> {
    return this.getConsignmentErrors();
  }
}

export interface MasterConsignmentDetailsComponentData {
  consignmentProcessId: string;
  crn: string,
  transportEquipmentMap: { [containerIdentificationNumber: string]: IE3TransportEquipment };
  cachedConsignmentProcess: IE3ConsignmentProcess;
  copyOfConsignmentProcessId?: string;
}

enum MainFilingChoice {
  YES = "Yes",
  NO = "No",
}

interface BillOfLadingFilingType {
  modeOfTransportType?: ModeOfTransportType;
  billOfLadingType?: BillOfLadingType;
  filing?: Filing;
}

enum BillOfLadingType {
  Straight = "Straight",
  Master = "Non-Straight"
}

enum Filing {
  Single = "Single",
  Multiple = "Multiple"
}

enum ModeOfTransportType {
  Maritime = "Maritime",
  Road = "Road"
}
